'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/lib/auth-context';
import TodoForm from './todos/TodoForm';

interface AddFollowUpTodoProps {
  decisionId: string;
  decisionTitle: string;
  showSuggestLink?: boolean;
}

export function AddFollowUpTodo({ decisionId, decisionTitle, showSuggestLink = true }: AddFollowUpTodoProps) {
  const router = useRouter();
  const { permissions, isLoading } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [success, setSuccess] = useState(false);

  if (isLoading || !permissions.canWrite) {
    return null;
  }

  const handleSubmit = async (data: {
    title: string;
    details?: string;
    due_date?: string;
    delegate?: string;
  }) => {
    try {
      const response = await fetch('/api/todos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          decision_id: decisionId,
        }),
      });

      if (!response.ok) throw new Error('Failed to create todo');

      setShowForm(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      router.refresh();
    } catch (error) {
      console.error('Failed to create follow-up todo:', error);
    }
  };

  if (success) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        Todo created
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
        <div className="mb-3 pb-3 border-b border-zinc-200 dark:border-zinc-700">
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Follow-up for: <span className="text-zinc-700 dark:text-zinc-300">{decisionTitle}</span>
          </p>
        </div>
        <TodoForm
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-8">
      <button
        onClick={() => setShowForm(true)}
        className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add follow-up todo
      </button>
      {showSuggestLink && permissions.canReflect && (
        <Link
          href={`/decisions/${decisionId}/suggest-todo`}
          className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-600 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Suggest follow-up todo
        </Link>
      )}
    </div>
  );
}
